import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Box,
  Typography,
  Chip,
  Autocomplete,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import {
  Send as SendIcon,
  Close as CloseIcon,
  Person as PersonIcon,
  CampaignOutlined as BroadcastIcon
} from '@mui/icons-material';
import dayjs from 'dayjs';
import 'dayjs/locale/de';
import {
  sendMessageToDriver,
  sendBroadcastMessage,
  createTestMessage,
  clearError,
  clearLastSentMessage
} from '../../redux/slices/driverMessagesSlice';
import brandingConfig from '../../config/brandingConfig';

dayjs.locale('de');

const SendMessageDialog = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const drivers = useSelector((state) => state.appData.drivers);
  const driversLoading = useSelector((state) => state.appData.isLoading.drivers);
  const { sendingMessage, error, lastSentMessage } = useSelector((state) => state.driverMessages);

  const [formData, setFormData] = useState({
    title: 'Zentrale',
    message: '',
    priority: 'normal',
    recipientType: 'individual',
    selectedDriver: null,
    expirationHours: 2,
    expiresAt: dayjs().add(2, 'hour')
  });

  const [formErrors, setFormErrors] = useState({});

  // AppDataManager lädt automatisch alle Fahrer-Daten
  // Kein manueller fetchDrivers() Call mehr nötig

  useEffect(() => {
    if (lastSentMessage) {
      handleClose();
      dispatch(clearLastSentMessage());
    }
  }, [lastSentMessage, dispatch]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error for this field
    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.title.trim()) {
      errors.title = 'Titel ist erforderlich';
    } else if (formData.title.length > 50) {
      errors.title = 'Titel darf maximal 50 Zeichen lang sein';
    }

    if (!formData.message.trim()) {
      errors.message = 'Nachricht ist erforderlich';
    } else if (formData.message.length > 200) {
      errors.message = 'Nachricht darf maximal 200 Zeichen lang sein';
    }

    if (formData.recipientType === 'individual' && !formData.selectedDriver) {
      errors.selectedDriver = 'Bitte wählen Sie einen Fahrer aus';
    }

    if (!formData.expiresAt || !dayjs(formData.expiresAt).isValid()) {
      errors.expiresAt = 'Bitte wählen Sie ein gültiges Ablaufdatum';
    }

    if (dayjs(formData.expiresAt).isBefore(dayjs())) {
      errors.expiresAt = 'Ablaufdatum muss in der Zukunft liegen';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSendMessage = async () => {
    if (!validateForm()) {
      return;
    }

    const messageData = {
      title: formData.title.trim(),
      message: formData.message.trim(),
      priority: formData.priority,
      expiresAt: formData.expiresAt?.toDate()
    };

    try {
      if (formData.recipientType === 'broadcast') {
        await dispatch(sendBroadcastMessage(messageData)).unwrap();
      } else {
        await dispatch(sendMessageToDriver({
          recipientId: formData.selectedDriver.id,
          ...messageData
        })).unwrap();
      }
    } catch (error) {
      console.error('Fehler beim Senden der Nachricht:', error);
    }
  };

  const handleSendTestMessage = async () => {
    try {
      const recipientId = formData.recipientType === 'broadcast' ? 'all' : formData.selectedDriver?.id;
      if (formData.recipientType === 'individual' && !recipientId) {
        setFormErrors({ selectedDriver: 'Bitte wählen Sie einen Fahrer für die Test-Nachricht aus' });
        return;
      }
      
      await dispatch(createTestMessage({ recipientId })).unwrap();
    } catch (error) {
      console.error('Fehler beim Senden der Test-Nachricht:', error);
    }
  };

  const handleClose = () => {
    setFormData({
      title: 'Zentrale',
      message: '',
      priority: 'normal',
      recipientType: 'individual',
      selectedDriver: null,
      expirationHours: 2,
      expiresAt: dayjs().add(2, 'hour')
    });
    setFormErrors({});
    dispatch(clearError());
    onClose();
  };

  const driverOptions = drivers.filter(driver => driver.isOnline === true).map(driver => ({
    id: driver.id,
    label: driver.name || 'Unbekannter Fahrer',
    phone: driver.phone
  }));


  return (
    <Dialog 
        open={open} 
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ 
          backgroundColor: brandingConfig.colors.primary, 
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <SendIcon />
          Nachricht an Fahrer senden
        </DialogTitle>

        <DialogContent sx={{ p: 3, pt: 8 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 3 }}>
            {/* Empfänger-Typ */}
            <FormControl fullWidth>
              <InputLabel>Empfänger</InputLabel>
              <Select
                value={formData.recipientType}
                label="Empfänger"
                onChange={(e) => handleInputChange('recipientType', e.target.value)}
              >
                <MenuItem value="individual">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon fontSize="small" />
                    Einzelner Fahrer
                  </Box>
                </MenuItem>
                <MenuItem value="broadcast">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BroadcastIcon fontSize="small" />
                    Alle Fahrer (Broadcast)
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>

            {/* Fahrer-Auswahl (nur bei individuellem Empfänger) */}
            {formData.recipientType === 'individual' && (
              <Autocomplete
                options={driverOptions}
                getOptionLabel={(option) => option.label}
                value={formData.selectedDriver}
                onChange={(event, newValue) => handleInputChange('selectedDriver', newValue)}
                loading={driversLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Fahrer auswählen"
                    error={!!formErrors.selectedDriver}
                    helperText={formErrors.selectedDriver}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {driversLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box>
                      <Typography variant="body1">{option.label}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.phone}
                      </Typography>
                    </Box>
                  </Box>
                )}
              />
            )}

            {/* Titel */}
            <TextField
              label="Titel"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              error={!!formErrors.title}
              helperText={formErrors.title || `${formData.title.length}/50 Zeichen`}
              inputProps={{ maxLength: 50 }}
              fullWidth
            />

            {/* Nachricht */}
            <TextField
              label="Nachricht"
              value={formData.message}
              onChange={(e) => handleInputChange('message', e.target.value)}
              error={!!formErrors.message}
              helperText={formErrors.message || `${formData.message.length}/200 Zeichen`}
              inputProps={{ maxLength: 200 }}
              multiline
              rows={4}
              fullWidth
            />

            {/* Priorität */}
            <FormControl fullWidth>
              <InputLabel>Priorität</InputLabel>
              <Select
                value={formData.priority}
                label="Priorität"
                onChange={(e) => handleInputChange('priority', e.target.value)}
              >
                <MenuItem value="normal">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip label="Normal" size="small" color="default" />
                    Normale Priorität
                  </Box>
                </MenuItem>
                <MenuItem value="high">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip label="Hoch" size="small" color="warning" />
                    Hohe Priorität
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>

            {/* Ablaufzeit */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Ablaufzeit der Nachricht
              </Typography>
              <ToggleButtonGroup
                value={formData.expirationHours}
                exclusive
                onChange={(event, newValue) => {
                  if (newValue !== null) {
                    handleInputChange('expirationHours', newValue);
                    handleInputChange('expiresAt', dayjs().add(newValue, 'hour'));
                  }
                }}
                aria-label="Ablaufzeit"
                size="small"
                fullWidth
                sx={{ mb: 2 }}
              >
                <ToggleButton value={2} aria-label="2 Stunden">
                  2h
                </ToggleButton>
                <ToggleButton value={6} aria-label="6 Stunden">
                  6h
                </ToggleButton>
                <ToggleButton value={12} aria-label="12 Stunden">
                  12h
                </ToggleButton>
              </ToggleButtonGroup>
              
              <Typography variant="caption" color="text.secondary">
                Nachricht läuft ab am: {formData.expiresAt ? dayjs(formData.expiresAt).format('DD.MM.YYYY HH:mm') : '-'}
              </Typography>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button
            onClick={handleSendTestMessage}
            variant="outlined"
            disabled={sendingMessage || (formData.recipientType === 'individual' && !formData.selectedDriver)}
            sx={{ mr: 'auto' }}
          >
            Test-Nachricht
          </Button>
          
          <Button
            onClick={handleClose}
            startIcon={<CloseIcon />}
            disabled={sendingMessage}
          >
            Abbrechen
          </Button>
          
          <Button
            onClick={handleSendMessage}
            variant="contained"
            startIcon={sendingMessage ? <CircularProgress size={20} /> : <SendIcon />}
            disabled={sendingMessage}
            sx={{
              backgroundColor: brandingConfig.colors.primary,
              '&:hover': {
                backgroundColor: brandingConfig.colors.primaryDark
              }
            }}
          >
            {sendingMessage ? 'Wird gesendet...' : 'Senden'}
          </Button>
        </DialogActions>
      </Dialog>
  );
};

export default SendMessageDialog;